import { useEffect, useState, useCallback } from "react";
import { AdminApi, type AdminUser, type AdminUserDetail, type PaginatedResponse } from "@/api/adminApi";
import { RestClient } from "@/api/restClient";

const apiUrl = import.meta.env.VITE_API_URL || "";
const client = new RestClient({ apiUrl });
const adminApi = new AdminApi(client);

export function AdminUsers() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<AdminUserDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.listUsers({ page, limit: 25, search: search || undefined });
      setUsers(res.items);
      setTotal(res.total);
    } catch {
      // error handling
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const openDetail = async (userId: string) => {
    setDetailLoading(true);
    try {
      const detail = await adminApi.getUserDetail(userId);
      setSelectedUser(detail);
    } catch {
      // error handling
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSuspend = async (userId: string) => {
    const reason = prompt("Suspension reason:");
    if (reason === null) return;
    await adminApi.suspendUser(userId, reason);
    loadUsers();
    if (selectedUser?.user_id === userId) openDetail(userId);
  };

  const handleUnsuspend = async (userId: string) => {
    await adminApi.unsuspendUser(userId);
    loadUsers();
    if (selectedUser?.user_id === userId) openDetail(userId);
  };

  return (
    <div style={{ display: "flex", gap: 24 }}>
      {/* List */}
      <div style={{ flex: 1 }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, marginBottom: 16 }}>Users</h1>

        {/* Search */}
        <div style={{ marginBottom: 16 }}>
          <input
            type="text"
            placeholder="Search by email or user ID..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{
              width: "100%",
              padding: "8px 12px",
              border: "1px solid var(--border-color, #e0e0e0)",
              borderRadius: 6,
              fontSize: 14,
            }}
          />
        </div>

        {/* Table */}
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid var(--border-color, #e0e0e0)" }}>
              <th style={{ textAlign: "left", padding: "8px 4px" }}>Email</th>
              <th style={{ textAlign: "left", padding: "8px 4px" }}>Orgs</th>
              <th style={{ textAlign: "left", padding: "8px 4px" }}>Last Active</th>
              <th style={{ textAlign: "left", padding: "8px 4px" }}>Status</th>
              <th style={{ textAlign: "right", padding: "8px 4px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && !users.length ? (
              <tr><td colSpan={5} style={{ padding: 16, textAlign: "center" }}>Loading...</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: 16, textAlign: "center" }}>No users found</td></tr>
            ) : (
              users.map((u) => (
                <tr
                  key={u.user_id}
                  onClick={() => openDetail(u.user_id)}
                  style={{
                    borderBottom: "1px solid var(--border-color, #f0f0f0)",
                    cursor: "pointer",
                    background: selectedUser?.user_id === u.user_id ? "var(--primary-bg, #eff6ff)" : undefined,
                  }}
                >
                  <td style={{ padding: "8px 4px" }}>{u.email || u.user_id}</td>
                  <td style={{ padding: "8px 4px" }}>{u.org_count}</td>
                  <td style={{ padding: "8px 4px", color: "var(--text-secondary, #666)" }}>
                    {u.last_active_at ? new Date(u.last_active_at).toLocaleDateString() : "Never"}
                  </td>
                  <td style={{ padding: "8px 4px" }}>
                    {u.suspended ? (
                      <span style={{ color: "red", fontWeight: 500 }}>Suspended</span>
                    ) : (
                      <span style={{ color: "green" }}>Active</span>
                    )}
                  </td>
                  <td style={{ padding: "8px 4px", textAlign: "right" }}>
                    {u.suspended ? (
                      <button onClick={(e) => { e.stopPropagation(); handleUnsuspend(u.user_id); }}
                        style={{ fontSize: 12, padding: "2px 8px", cursor: "pointer" }}>
                        Unsuspend
                      </button>
                    ) : (
                      <button onClick={(e) => { e.stopPropagation(); handleSuspend(u.user_id); }}
                        style={{ fontSize: 12, padding: "2px 8px", cursor: "pointer", color: "red" }}>
                        Suspend
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16, fontSize: 13 }}>
          <span style={{ color: "var(--text-secondary, #666)" }}>
            {total} users total
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} style={{ cursor: "pointer" }}>
              ← Prev
            </button>
            <span>Page {page}</span>
            <button disabled={users.length < 25} onClick={() => setPage(page + 1)} style={{ cursor: "pointer" }}>
              Next →
            </button>
          </div>
        </div>
      </div>

      {/* Detail Panel */}
      {selectedUser && (
        <div style={{
          width: 360,
          borderLeft: "1px solid var(--border-color, #e0e0e0)",
          paddingLeft: 24,
          fontSize: 13,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>User Detail</h2>
            <button onClick={() => setSelectedUser(null)} style={{ cursor: "pointer", fontSize: 18, border: "none", background: "none" }}>×</button>
          </div>

          {detailLoading ? (
            <p>Loading...</p>
          ) : (
            <>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 500 }}>{selectedUser.email}</div>
                <div style={{ color: "var(--text-secondary, #666)", fontSize: 11 }}>{selectedUser.user_id}</div>
                {selectedUser.is_platform_admin && (
                  <span style={{ fontSize: 10, background: "#fef3c7", color: "#92400e", padding: "2px 6px", borderRadius: 4, marginTop: 4, display: "inline-block" }}>
                    Platform Admin
                  </span>
                )}
              </div>

              <div style={{ marginBottom: 12 }}>
                <h3 style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Memberships</h3>
                {selectedUser.memberships.map((m) => (
                  <div key={m.company_id} style={{ padding: "4px 0", borderBottom: "1px solid var(--border-color, #f0f0f0)" }}>
                    <div>{m.company_name || m.company_id}</div>
                    <div style={{ fontSize: 11, color: "var(--text-secondary, #666)" }}>
                      {m.suspended ? "Suspended" : "Active"}
                      {m.last_active_at && ` · Last active ${new Date(m.last_active_at).toLocaleDateString()}`}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
                <div style={{ padding: 8, background: "var(--surface-bg, #f9f9f9)", borderRadius: 4 }}>
                  <div style={{ fontSize: 11, color: "var(--text-secondary, #666)" }}>Sessions (30d)</div>
                  <div style={{ fontWeight: 600 }}>{selectedUser.sessions_30d}</div>
                </div>
                <div style={{ padding: 8, background: "var(--surface-bg, #f9f9f9)", borderRadius: 4 }}>
                  <div style={{ fontSize: 11, color: "var(--text-secondary, #666)" }}>Facts</div>
                  <div style={{ fontWeight: 600 }}>{selectedUser.facts_count}</div>
                </div>
                <div style={{ padding: 8, background: "var(--surface-bg, #f9f9f9)", borderRadius: 4 }}>
                  <div style={{ fontSize: 11, color: "var(--text-secondary, #666)" }}>Schedules</div>
                  <div style={{ fontWeight: 600 }}>{selectedUser.schedules_active}</div>
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  onClick={() => handleSuspend(selectedUser.user_id)}
                  style={{ fontSize: 12, padding: "4px 12px", cursor: "pointer", color: "red", border: "1px solid red", borderRadius: 4, background: "transparent" }}
                >
                  Suspend
                </button>
                <button
                  onClick={async () => {
                    const res = await adminApi.impersonateUser(selectedUser.user_id);
                    navigator.clipboard.writeText(res.token);
                    alert(`Impersonation token copied (expires in ${res.expires_in}s)`);
                  }}
                  style={{ fontSize: 12, padding: "4px 12px", cursor: "pointer", border: "1px solid var(--border-color, #ccc)", borderRadius: 4, background: "transparent" }}
                >
                  Impersonate
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
